import type { UserRow } from "./sql";

// The user_roles spine returns one row per (user, company). KPIs, the butterfly,
// the cohort grid, the activation-over-time chart, etc. all report DISTINCT
// USERS — but the collapse is filter-then-distinct, never distinct-then-filter.
//
// 1. Apply every active filter to the per-pair rowset first (already done by
//    applyFilters in the page).
// 2. Then collapse to distinct users: pick a single representative row per
//    user using `pickRepresentative` and use `bool_or(predicate)` semantics
//    across each user's surviving pair rows for stage hits.
//
// A user who is admin in workspace A and support in workspace B keeps their
// admin row when excludeSupport is on; a user who is support everywhere drops
// out entirely.

// Pick the most billing-relevant row per user. Higher-tier plan wins, then
// converted_after_signup, then synopsis_count. Mirrors the old dedupeByUser
// scoring so KPI numbers stay stable across the refactor.
function rankPlan(plan: string | null): number {
  if (plan === "pro") return 3;
  if (plan === "plus") return 2;
  if (plan === "free") return 1;
  return 0;
}

function score(r: UserRow): number {
  return rankPlan(r.billing_plan) * 1000 + (r.converted_after_signup ? 500 : 0) + r.synopsis_count;
}

// Collapse per-pair rows to one row per distinct user. The returned UserRow
// carries an `on_paid_plan` / `had_demo` etc. that's the bool_or across all
// surviving pair rows for that user (so a user who activated in any workspace
// counts as activated). Activity counts are summed.
export function collapseToDistinctUsers(rows: UserRow[]): UserRow[] {
  const groups = new Map<string, UserRow[]>();
  for (const r of rows) {
    const arr = groups.get(r.user_external_id) ?? [];
    arr.push(r);
    groups.set(r.user_external_id, arr);
  }
  const out: UserRow[] = [];
  for (const arr of groups.values()) {
    const rep = [...arr].sort((a, b) => score(b) - score(a))[0];
    if (!rep) continue;
    const merged: UserRow = {
      ...rep,
      // Sum activity counts so a user active in two workspaces shows their
      // total activity. Matches today's KPI behaviour where dedupeByUser kept
      // the highest-scoring row but the rep now also benefits from secondary
      // workspace activity.
      transcript_count: arr.reduce((s, r) => s + r.transcript_count, 0),
      synopsis_count: arr.reduce((s, r) => s + r.synopsis_count, 0),
      chat_meeting_count: arr.reduce((s, r) => s + r.chat_meeting_count, 0),
      chat_client_count: arr.reduce((s, r) => s + r.chat_client_count, 0),
      custom_template_count: arr.reduce((s, r) => s + r.custom_template_count, 0),
      // bool_or across pair rows: stage hits OR.
      calendar_connected: arr.some((r) => r.calendar_connected),
      on_paid_plan: arr.some((r) => r.on_paid_plan),
      converted_after_signup: arr.some((r) => r.converted_after_signup),
      // Earliest first-event date wins.
      first_transcript_date: minDate(arr.map((r) => r.first_transcript_date)),
      first_synopsis_date: minDate(arr.map((r) => r.first_synopsis_date)),
      first_calendar_date: minDate(arr.map((r) => r.first_calendar_date)),
      first_chat_meeting_date: minDate(arr.map((r) => r.first_chat_meeting_date)),
      first_chat_client_date: minDate(arr.map((r) => r.first_chat_client_date)),
      first_custom_template_date: minDate(arr.map((r) => r.first_custom_template_date)),
      last_active_date: maxDate(arr.map((r) => r.last_active_date)),
    };
    out.push(merged);
  }
  return out;
}

function minDate(dates: (string | null)[]): string | null {
  let out: string | null = null;
  for (const d of dates) {
    if (!d) continue;
    if (out == null || d < out) out = d;
  }
  return out;
}

function maxDate(dates: (string | null)[]): string | null {
  let out: string | null = null;
  for (const d of dates) {
    if (!d) continue;
    if (out == null || d > out) out = d;
  }
  return out;
}

// Distinct-user count without the full collapse (faster when only the count
// is needed).
export function distinctUserCount(rows: UserRow[]): number {
  const set = new Set<string>();
  for (const r of rows) set.add(r.user_external_id);
  return set.size;
}
